import { fetchAndActivate, getBoolean, getString } from 'firebase/remote-config';
import { isFirebaseConfigured, remoteConfig } from '../firebase';

export interface RemoteConfigValues {
  enable_payment: boolean;
  require_subscription: boolean;
  enable_signup: boolean;
  enable_new_pallete: boolean;
  support_email: string;
  login_url: string;
  max_file_size_mb: number;
  enable_languages: boolean;
  client_password_min_length: number;
  client_password_min_uppercase: number;
  client_password_min_lowercase: number;
  client_password_min_numbers: number;
  client_password_min_special_chars: number;
}

class RemoteConfigService {
  private values: RemoteConfigValues = {
    enable_payment: true,
    require_subscription: true,
    enable_signup: true,
    enable_new_pallete: import.meta.env.VITE_ENABLE_NEW_PALETTE === 'true',
    support_email: 'kontakt@centrumcoucha.pl',
    login_url: '',
    max_file_size_mb: 5,
    enable_languages: true,
    client_password_min_length: 10,
    client_password_min_uppercase: 1,
    client_password_min_lowercase: 1,
    client_password_min_numbers: 1,
    client_password_min_special_chars: 1,
  };
  private initialized = false;
  private initPromise: Promise<RemoteConfigValues> | null = null;

  async init(): Promise<RemoteConfigValues> {
    if (this.initialized) return this.values;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      if (isFirebaseConfigured && remoteConfig) {
        try {
          // Pobieramy i aktywujemy konfigurację raz przy starcie
          await fetchAndActivate(remoteConfig);
          this.values.enable_payment = getBoolean(remoteConfig, 'enable_payment');
          this.values.require_subscription = getBoolean(remoteConfig, 'require_subscription');
          this.values.enable_signup = getBoolean(remoteConfig, 'enable_signup');
          this.values.enable_new_pallete = getBoolean(remoteConfig, 'enable_new_pallete');
          this.values.support_email = getString(remoteConfig, 'support_email') || 'kontakt@centrumcoucha.pl';
          this.values.login_url = getString(remoteConfig, 'login_url') || '';
          this.values.max_file_size_mb = parseInt(getString(remoteConfig, 'max_file_size_mb'), 10) || 5;
          this.values.enable_languages = getBoolean(remoteConfig, 'enable_languages');
          const minLenStr = getString(remoteConfig, 'client_password_min_length');
          this.values.client_password_min_length = minLenStr ? (parseInt(minLenStr, 10) || 10) : 10;
          const minUpperStr = getString(remoteConfig, 'client_password_min_uppercase');
          this.values.client_password_min_uppercase = minUpperStr !== '' ? (parseInt(minUpperStr, 10) ?? 1) : 1;
          const minLowerStr = getString(remoteConfig, 'client_password_min_lowercase');
          this.values.client_password_min_lowercase = minLowerStr !== '' ? (parseInt(minLowerStr, 10) ?? 1) : 1;
          const minNumbersStr = getString(remoteConfig, 'client_password_min_numbers');
          this.values.client_password_min_numbers = minNumbersStr !== '' ? (parseInt(minNumbersStr, 10) ?? 1) : 1;
          const minSpecialStr = getString(remoteConfig, 'client_password_min_special_chars');
          this.values.client_password_min_special_chars = minSpecialStr !== '' ? (parseInt(minSpecialStr, 10) ?? 1) : 1;
          
          const isDebug = import.meta.env.DEV || (typeof window !== 'undefined' && (window.location.hostname.includes('stg') || window.location.hostname.includes('staging') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
          if (isDebug) {
            console.log("📡 [RemoteConfigService] Załadowano konfigurację zdalną:", this.values);
          }
        } catch (error) {
          console.error("❌ [RemoteConfigService] Błąd pobierania konfiguracji zdalnej:", error);
        }
      } else {
        const isDebug = import.meta.env.DEV || (typeof window !== 'undefined' && (window.location.hostname.includes('stg') || window.location.hostname.includes('staging') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
        if (isDebug) {
          console.log("📡 [RemoteConfigService] Brak połączenia z Firebase (tryb DEMO). Używam domyślnych wartości:", this.values);
        }
      }
      this.initialized = true;
      return this.values;
    })();

    return this.initPromise;
  }

  get enablePayment(): boolean {
    return this.values.enable_payment;
  }

  get requireSubscription(): boolean {
    return this.values.require_subscription;
  }

  get enableSignup(): boolean {
    return this.values.enable_signup;
  }

  get enableNewPalette(): boolean {
    return this.values.enable_new_pallete;
  }

  get supportEmail(): string {
    return this.values.support_email;
  }

  get loginUrl(): string {
    return this.values.login_url;
  }

  get maxFileSizeMb(): number {
    return this.values.max_file_size_mb;
  }

  get enableLanguages(): boolean {
    return this.values.enable_languages;
  }

  get clientPasswordMinLength(): number {
    return this.values.client_password_min_length;
  }

  get clientPasswordMinUppercase(): number {
    return this.values.client_password_min_uppercase;
  }

  get clientPasswordMinLowercase(): number {
    return this.values.client_password_min_lowercase;
  }

  get clientPasswordMinNumbers(): number {
    return this.values.client_password_min_numbers;
  }

  get clientPasswordMinSpecialChars(): number {
    return this.values.client_password_min_special_chars;
  }
}

export const remoteConfigService = new RemoteConfigService();
export default remoteConfigService;
